import { Button, Checkbox, Tooltip } from 'antd'
import { DeleteOutlined, EditOutlined, FolderOpenOutlined, SaveOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { triggerBrowserDownload, type AssetChainOut } from '../api/client'
import { stageStatusColor } from '../utils/status'
import { FourStageImageFlow, bustUrl } from './StageFlow'

/** Appends a cache-busting query param so the browser always fetches fresh
 * bytes after a stage is regenerated, even though the underlying file path
 * never changes. `updated_at` is used for `original` (no dedicated revision
 * counter — it's immutable once uploaded, spec: `original_missing` aside).
 * transparent/background/shadow MUST key off their own
 * `transparent_generation`/`background_generation`/`shadow_generation`
 * (ASSET-UX-CONSISTENCY-A1, extended to transparent by Image Asset Loading
 * Performance P0) — those are bumped exactly once per genuinely new
 * artifact, whereas `updated_at` changes on every save to the row
 * (including an unrelated stage's save) and would otherwise bust/re-
 * download an unchanged stage's thumbnail/full-res URL on every one of
 * those, and can be stale/out-of-order relative to the actual bytes at
 * that URL if two responses race (see `loadChains`'s request-counter guard
 * in `Home.tsx`/`ImagesPage.tsx`). */
function bust(url: string | null | undefined, chain: AssetChainOut): string | null {
  return bustUrl(url, chain.updated_at)
}

function bustTransparent(url: string | null | undefined, chain: AssetChainOut): string | null {
  return bustUrl(url, chain.transparent_generation)
}

function bustBackground(url: string | null | undefined, chain: AssetChainOut): string | null {
  return bustUrl(url, chain.background_generation)
}

function bustShadow(url: string | null | undefined, chain: AssetChainOut): string | null {
  return bustUrl(url, chain.shadow_generation)
}

/**
 * The single "一张图片" row — checkbox + 原图→透明底图→色底图→阴影图 (via the
 * shared `FourStageImageFlow`) + per-row actions. Phase 2B-2 (spec 十二):
 * extracted out of `Home.tsx` verbatim so the standalone asset list and the
 * SaaS Workspace Images page render/behave identically and can never drift
 * into two parallel implementations — `onReveal` is the one prop the SaaS
 * caller omits (a SaaS server has no local filesystem to "打开文件夹" into,
 * spec 十一), which simply hides that one button.
 */
export default function ChainRow({
  chain,
  selected,
  onToggle,
  onEdit,
  onEditBackground,
  onEditShadow,
  onRetryStage,
  onDeleteOriginal,
  onDeleteTransparent,
  onDeleteBackground,
  onDeleteShadow,
  onDeleteChain,
  onRename,
  onReprocess,
  onSaveAsTemplate,
  onReveal,
}: {
  chain: AssetChainOut
  selected: boolean
  onToggle: () => void
  onEdit: () => void
  onEditBackground: () => void
  onEditShadow: () => void
  onRetryStage: (stage: 'transparent' | 'background' | 'shadow') => void
  onDeleteOriginal: () => void
  onDeleteTransparent: () => void
  onDeleteBackground: () => void
  onDeleteShadow: () => void
  onDeleteChain: () => void
  onRename: () => void
  onReprocess: () => void
  onSaveAsTemplate: () => void
  /** Omit to hide "打开最终成果文件夹" (spec 十一 — SaaS has no local fs). */
  onReveal?: () => void
}) {
  const { t } = useTranslation('autoProcess')

  return (
    <div className={`chain-row ${selected ? 'chain-row-selected' : ''}`} data-testid="chain-row" data-chain-id={chain.id}>
      <div className="chain-cell chain-cell-select">
        <Checkbox checked={selected} onChange={onToggle} data-testid="chain-select-checkbox" />
      </div>

      <FourStageImageFlow
        original={{
          url: chain.original_missing ? null : bust(chain.thumbnail_url, chain),
          previewUrl: chain.original_missing ? null : bust(chain.proxy_url || chain.original_url, chain),
          fullUrl: chain.original_missing ? null : bust(chain.original_url, chain),
          downloadUrl: chain.original_download_url || undefined,
          viewerKind: 'original',
          statusLabel: chain.original_missing ? t('stages.originalMissing') : undefined,
          statusColor: chain.original_missing ? stageStatusColor.failed : undefined,
          subtitle: chain.original_filename,
          meta: chain.original_width && chain.original_height ? `${chain.original_width}×${chain.original_height}` : undefined,
          onDelete: onDeleteOriginal,
          onDownload: chain.original_download_url ? () => triggerBrowserDownload(chain.original_download_url!) : undefined,
        }}
        transparent={{
          checker: !!chain.transparent_url,
          url: bustTransparent(chain.transparent_thumb_url, chain),
          previewUrl: bustTransparent(chain.transparent_proxy_url || chain.transparent_url, chain),
          fullUrl: bustTransparent(chain.transparent_url, chain),
          downloadUrl: chain.transparent_download_url || undefined,
          viewerKind: 'transparent',
          statusLabel: chain.transparent_status_label,
          statusColor: stageStatusColor[chain.transparent_status],
          error: chain.last_error_stage === 'transparent' ? chain.last_error_message : undefined,
          onDelete: chain.transparent_url ? onDeleteTransparent : undefined,
          onDownload: chain.transparent_download_url
            ? () => triggerBrowserDownload(chain.transparent_download_url!)
            : undefined,
          onRetry: () => onRetryStage('transparent'),
          showRetry: true,
        }}
        background={{
          url: bustBackground(chain.background_thumb_url, chain),
          previewUrl: bustBackground(chain.background_url, chain),
          fullUrl: bustBackground(chain.background_url, chain),
          downloadUrl: chain.background_download_url || undefined,
          viewerKind: 'background',
          statusLabel: chain.background_status_label,
          statusColor: stageStatusColor[chain.background_status],
          error: chain.last_error_stage === 'background' ? chain.last_error_message : undefined,
          onDelete: chain.background_url ? onDeleteBackground : undefined,
          onDownload: chain.background_download_url
            ? () => triggerBrowserDownload(chain.background_download_url!)
            : undefined,
          onRetry: () => onRetryStage('background'),
          showRetry: chain.transparent_status === 'done',
          busy: chain.background_status === 'queued' || chain.background_status === 'processing',
        }}
        shadow={{
          url: bustShadow(chain.shadow_thumb_url, chain),
          previewUrl: bustShadow(chain.shadow_url, chain),
          fullUrl: bustShadow(chain.shadow_url, chain),
          downloadUrl: chain.shadow_download_url || undefined,
          viewerKind: 'shadow',
          statusLabel: chain.shadow_status_label,
          statusColor: stageStatusColor[chain.shadow_status],
          error: chain.last_error_stage === 'shadow' ? chain.last_error_message : undefined,
          onDelete: chain.shadow_url ? onDeleteShadow : undefined,
          onDownload: chain.shadow_download_url ? () => triggerBrowserDownload(chain.shadow_download_url!) : undefined,
          onRetry: () => onRetryStage('shadow'),
          showRetry: chain.background_status === 'done',
          busy: chain.shadow_status === 'queued' || chain.shadow_status === 'processing',
        }}
        toTransparent={{ onClick: onEdit, disabled: chain.original_missing, label: t('stages.editTransparent') }}
        toBackground={{ onClick: onEditBackground, disabled: chain.transparent_status !== 'done', label: t('stages.editBackground') }}
        toShadow={{ onClick: onEditShadow, disabled: chain.background_status !== 'done', label: t('stages.editShadow') }}
      />

      <div className="chain-cell chain-actions-cell">
        {chain.process_template_name && (
          <Tooltip title={t('stages.processTemplate', { name: chain.process_template_name })}>
            <span className="chain-template-tag">{chain.process_template_name}</span>
          </Tooltip>
        )}
        <Button size="small" onClick={onReprocess} className="action-primary-btn" data-testid="chain-reprocess-btn">
          {t('stages.reprocessAll')}
        </Button>
        <div className="action-icon-row">
          <Tooltip title={t('stages.rename')}>
            <Button size="small" icon={<EditOutlined />} onClick={onRename} data-testid="chain-rename-btn" />
          </Tooltip>
          <Tooltip title={t('stages.saveAsTemplate')}>
            <Button size="small" icon={<SaveOutlined />} onClick={onSaveAsTemplate} data-testid="chain-save-as-template-btn" />
          </Tooltip>
          {onReveal && (
            <Tooltip title={t('stages.revealFolder')}>
              <Button size="small" icon={<FolderOpenOutlined />} onClick={onReveal} data-testid="chain-reveal-btn" />
            </Tooltip>
          )}
          <Tooltip title={t('stages.deleteChain')}>
            <Button size="small" danger icon={<DeleteOutlined />} onClick={onDeleteChain} data-testid="chain-delete-btn" />
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
